/**
 * useOrder Hook
 *
 * Fetches a single order by ID using URQL.
 */

import { useEffect } from "react";
import { useQuery } from "urql";
import type { Order } from "@/generated/graphql";
import { getUrqlClient } from "@/api/client";
import { createLogger } from "@/lib/logger";

const orderLog = createLogger("order");

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const OrderDocument = `
  query Order($id: ID!) {
    order(id: $id) {
      id
      status
      subtotal
      feesTotal
      totalAmount
      currency
      expiresAt
      event {
        id
        title
        startsAt
        venue
        imageUrl
      }
      items {
        id
        quantity
        unitPrice
        totalPrice
        ticketType {
          id
          name
        }
      }
      fees {
        id
        name
        type
        amount
      }
      pricingOptions {
        id
        currency
        subtotal
        feesTotal
        total
        isSelected
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
      selectedPricing {
        id
        currency
        subtotal
        feesTotal
        total
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
      manualPayment {
        status
        receiptUrl
      }
      tickets {
        id
        ticketNumber
        ticketType {
          name
        }
        event {
          id
          title
          startsAt
          venue
        }
      }
      conversationMessages {
        id
        messageText
        createdAt
        isFromUser
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseOrderOptions {
  /** Whether to pause the query */
  pause?: boolean;
  /** Polling interval in ms (for payment status checking) */
  pollInterval?: number;
}

interface UseOrderResult {
  order: Order | null;
  /** True only on initial load (no cached data yet) */
  isLoading: boolean;
  /** True when fetching (including refetch with cached data) */
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch a single order by ID.
 *
 * Uses URQL for normalized caching. After mutations like proceedToPayment,
 * the cache automatically invalidates and refetches.
 */
export function useOrder(
  orderId: string | undefined,
  options: UseOrderOptions = {}
): UseOrderResult {
  const { pause = false, pollInterval } = options;

  const [result, reexecuteQuery] = useQuery<{ order: Order | null }>({
    query: OrderDocument,
    variables: { id: orderId },
    pause: pause || !orderId,
  });

  // Polling support for payment status updates
  useEffect(() => {
    if (!pollInterval || pause || !orderId) return;

    const interval = setInterval(() => {
      reexecuteQuery({ requestPolicy: "network-only" });
    }, pollInterval);

    return () => clearInterval(interval);
  }, [pollInterval, pause, orderId, reexecuteQuery]);

  // isLoading = true only when fetching AND no data yet (initial load)
  // isFetching = true whenever a request is in flight (including refetch)
  const hasData = !!result.data?.order;
  
  return {
    order: result.data?.order ?? null,
    isLoading: result.fetching && !hasData,
    isFetching: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}

// =============================================================================
// STANDALONE FETCH (for use outside React components)
// =============================================================================

/**
 * Fetch order directly using URQL client.
 * Use this when you need to fetch outside of React component lifecycle.
 * For React components, prefer useOrder hook.
 */
export async function fetchOrder(orderId: string): Promise<Order | null> {
  try {
    const client = getUrqlClient();
    const result = await client
      .query<{ order: Order | null }>(OrderDocument, { id: orderId })
      .toPromise();
    
    if (result.error) {
      orderLog.error("fetchOrder error:", result.error.message);
      return null;
    }
    
    return result.data?.order ?? null;
  } catch {
    return null;
  }
}
